import { alpha, createTheme } from '@mui/material/styles';
import { ADMIN_LAYOUT } from './adminThemeTokens';

const PRIMARY = '#059669'; // Kirana Emerald Accent
const PRIMARY_LIGHT = '#10B981';
const PRIMARY_DARK = '#047857';
const SECONDARY = '#6366F1'; // Modern Indigo Secondary
const INFO = '#0284C7';
const SUCCESS = '#059669';
const WARNING = '#D97706';
const ERROR = '#DC2626';
const NO_SHADOWS = Array(25).fill('none');

const APP_FONT = '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
};

export const THEME_MODE_STORAGE_KEY = 'skhool-theme-mode';

export const resolveThemeMode = (value) =>
  value === THEME_MODES.DARK ? THEME_MODES.DARK : THEME_MODES.LIGHT;

export const createAppTheme = (mode = THEME_MODES.LIGHT) => {
  const resolvedMode = resolveThemeMode(mode);
  const isDark = resolvedMode === THEME_MODES.DARK;
  const textPrimary = isDark ? '#EEF0FF' : '#1B1E2B';
  const textSecondary = isDark ? '#B0B6D1' : '#5A6275';
  const divider = isDark ? alpha('#8D95B3', 0.18) : alpha('#8C92A8', 0.12);
  const surfaceBorder = isDark ? alpha('#A2AAC7', 0.16) : alpha('#9AA1B8', 0.16);
  const surfaceBorderStrong = isDark ? alpha('#B8C0DD', 0.22) : alpha('#8B92A8', 0.2);
  const backgroundDefault = isDark ? '#0B0F1A' : '#F3F6FF';
  const backgroundPaper = isDark ? '#141927' : '#FFFFFF';
  const fieldBackground = isDark ? alpha('#1A2234', 0.76) : '#F7F9FB';
  const rowStripedBackground = isDark ? alpha('#FFFFFF', 0.02) : alpha('#000000', 0.02);
  const rowHoverBackground = isDark ? alpha('#FFFFFF', 0.04) : alpha('#000000', 0.035);
  const appBarBackground = alpha(backgroundPaper, isDark ? 0.84 : 0.9);
  const menuBackground = isDark
    ? `linear-gradient(180deg, ${alpha('#1B2236', 0.98)} 0%, ${alpha('#141B2D', 0.98)} 100%)`
    : `linear-gradient(180deg, ${alpha(PRIMARY_LIGHT, 0.12)} 0%, ${alpha('#E8F1FF', 0.92)} 100%)`;
  const menuItemHoverBackground = isDark ? alpha(PRIMARY_LIGHT, 0.16) : alpha(PRIMARY, 0.08);
  const menuItemSelectedBackground = isDark ? alpha(PRIMARY_LIGHT, 0.24) : alpha(PRIMARY, 0.14);

  return createTheme({
    shadows: NO_SHADOWS,
    palette: {
      mode: resolvedMode,
      primary: {
        main: PRIMARY,
        light: PRIMARY_LIGHT,
        dark: PRIMARY_DARK,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: SECONDARY,
        light: '#14B8A6',
        dark: '#115E59',
        contrastText: '#FFFFFF',
      },
      info: {
        main: INFO,
        light: '#06B6D4',
        dark: '#155E75',
        contrastText: '#FFFFFF',
      },
      success: {
        main: SUCCESS,
        light: '#22C55E',
        dark: '#14532D',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: WARNING,
        light: '#EAB308',
        dark: '#A16207',
        contrastText: isDark ? '#111827' : '#1F2937',
      },
      error: {
        main: ERROR,
        light: '#DC2626',
        dark: '#7F1D1D',
        contrastText: '#FFFFFF',
      },
      background: {
        default: backgroundDefault,
        paper: backgroundPaper,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
        disabled: isDark ? '#64748B' : '#98A2B3',
      },
      divider,
    },
    
    // REVAMPED TYPOGRAPHY
    typography: {
      fontFamily: APP_FONT,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em' },
      h2: { fontSize: '2rem', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h3: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.015em' },
      h4: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' },
      h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0em' },
      h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0em' },
      subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0em' },
      subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0em' },
      body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0em' },
      body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.57, letterSpacing: '0em' },
      button: { fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none', lineHeight: 1.75, letterSpacing: '0.01em' },
      caption: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.6, letterSpacing: '0.01em' },
      overline: { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', lineHeight: 1.5, letterSpacing: '0.08em' },
    },
    
    shape: {
      borderRadius: 6,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--brand-primary': PRIMARY,
            '--brand-secondary': SECONDARY,
            '--brand-info': INFO,
            '--brand-surface': backgroundPaper,
            '--brand-outline': divider,
            '--brand-text': textPrimary,
            '--admin-content-max-width': `${ADMIN_LAYOUT.contentMaxWidth}px`,
            '--admin-container-pad-xs': `${ADMIN_LAYOUT.containerPaddingPx.xs}px`,
            '--admin-container-pad-sm': `${ADMIN_LAYOUT.containerPaddingPx.sm}px`,
            '--admin-container-pad-md': `${ADMIN_LAYOUT.containerPaddingPx.md}px`,
          },
          '@keyframes admin-page-enter': {
            '0%': { opacity: 0, transform: 'translateY(8px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes admin-highlight-pulse': {
            '0%, 100%': { backgroundColor: alpha(PRIMARY, isDark ? 0.12 : 0.04) },
            '50%': { backgroundColor: alpha(PRIMARY, isDark ? 0.2 : 0.08) },
          },
          body: {
            backgroundColor: backgroundDefault,
            color: textPrimary,
            colorScheme: resolvedMode,
            fontFamily: APP_FONT,
            textRendering: 'optimizeLegibility',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale', // Added for better Mac rendering
          },
          'button, input, textarea, select, .MuiTypography-root, .MuiInputBase-input': {
            fontFamily: APP_FONT,
          },
          '#root': {
            minHeight: '100vh',
          },
          '.admin-page-content > *': {
            animation: 'admin-page-enter 240ms ease-out both',
          },
          '.admin-kpi-pulse': {
            animation: 'admin-highlight-pulse 2s ease-in-out infinite',
          },
          '.admin-page-shell .admin-page-content': {
            gap: '12px',
          },
          '.admin-page-shell .MuiPaper-root, .admin-page-shell .MuiCard-root, .admin-page-shell .MuiTableContainer-root': {
            border: 'none !important',
            backgroundColor: `${backgroundPaper} !important`,
            backgroundImage: 'none !important',
            borderRadius: '6px !important',
            boxShadow: 'none !important',
            transform: 'none !important',
          },
          '.admin-page-shell .MuiOutlinedInput-root, .admin-page-shell .MuiButton-root, .admin-page-shell .MuiChip-root, .admin-page-shell .MuiAlert-root, .admin-page-shell .MuiDialog-paper, .admin-page-shell .MuiMenu-paper, .admin-page-shell .MuiPopover-paper': {
            borderRadius: '6px !important',
          },
          '.MuiPaper-root, .MuiCard-root, .MuiDrawer-paper, .MuiPopover-paper, .MuiMenu-paper, .MuiDialog-paper, .MuiAlert-root': {
            boxShadow: 'none !important',
          },
          '.admin-page-shell .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
            backgroundColor: 'transparent',
          },
          '.admin-page-shell .MuiTableHead-root .MuiTableCell-root': {
            borderBottom: `1px solid ${alpha(divider, 0.6)}`,
          },
          '.admin-page-shell .MuiTableBody-root .MuiTableCell-root': {
            borderBottom: `1px solid ${alpha(divider, 0.5)}`,
          },
          '.admin-page-shell[role="progressbar"]': {
            display: 'none !important',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'none',
            backgroundColor: appBarBackground,
            borderBottom: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: 8,
            backgroundImage: 'none',
            backgroundColor: backgroundPaper,
            color: textPrimary,
            boxShadow: 'none',
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            borderColor: 'transparent',
            borderWidth: 1,
            borderRadius: 8,
            backgroundColor: backgroundPaper,
            color: textPrimary,
            boxShadow: 'none',
            transition: 'transform 200ms ease, border-color 200ms ease, background-color 200ms ease',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 16,
            color: textPrimary,
            '&:last-child': {
              paddingBottom: 16,
            },
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: { padding: '12px 18px 14px', gap: 12 },
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 8,
            boxShadow: 'none',
            '&:before': { display: 'none' },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            paddingInline: 18,
            minHeight: 40,
            fontWeight: 600,
            boxShadow: 'none',
            transition: 'transform 180ms ease, background-color 180ms ease, border-color 180ms ease',
            '&:hover': { transform: 'translateY(-1px)' },
            '&:active': { transform: 'translateY(0)' },
          },
          contained: {
            backgroundColor: PRIMARY,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: alpha(PRIMARY, isDark ? 0.9 : 0.85),
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(PRIMARY, isDark ? 0.35 : 0.25),
              color: alpha('#FFFFFF', 0.7),
            },
          },
          outlined: {
            borderColor: alpha(textSecondary, 0.3),
            color: textPrimary,
            '&:hover': {
              borderColor: alpha(textSecondary, 0.45),
              backgroundColor: alpha(textSecondary, isDark ? 0.12 : 0.06),
            },
          },
          // ... color specific overrides kept intact ...
          containedPrimary: { backgroundColor: PRIMARY, color: '#FFFFFF' },
          containedSecondary: { backgroundColor: SECONDARY, color: '#FFFFFF' },
          containedSuccess: { backgroundColor: SUCCESS, color: '#FFFFFF' },
          containedWarning: { backgroundColor: WARNING, color: '#FFFFFF' },
          containedError: { backgroundColor: ERROR, color: '#FFFFFF' },
          outlinedSuccess: { borderColor: alpha(SUCCESS, isDark ? 0.3 : 0.24), color: textPrimary },
          outlinedWarning: { borderColor: alpha(WARNING, isDark ? 0.3 : 0.24), color: textPrimary },
          outlinedError: { borderColor: alpha(ERROR, isDark ? 0.3 : 0.24), color: textPrimary },
        },
      },
      MuiIconButton: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: 8,
            transition: 'background-color 180ms ease, transform 180ms ease',
            '&:hover': { transform: 'translateY(-1px)' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: fieldBackground,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: surfaceBorderStrong,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(PRIMARY, 0.55),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: PRIMARY,
            },
          },
          input: {
            paddingTop: 12,
            paddingBottom: 12,
            '&.MuiInputBase-inputSizeSmall': {
              paddingTop: 8,
              paddingBottom: 8,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            display: 'flex',
            alignItems: 'center',
            minHeight: 36,
            paddingTop: 8,
            paddingBottom: 8,
            '&.MuiInputBase-inputSizeSmall': {
              minHeight: 32,
              paddingTop: 6,
              paddingBottom: 6,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { alignItems: 'center' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            border: `1px solid ${surfaceBorder}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderColor: surfaceBorder,
            borderRadius: 0,
            boxShadow: 'none',
          },
        },
      },
      MuiTable: {
        defaultProps: { size: 'small' },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(divider, 0.35)}`,
            borderRadius: 16,
            boxShadow: 'none',
            backgroundColor: backgroundPaper,
            overflow: 'hidden',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(textSecondary, isDark ? 0.08 : 0.05),
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 180ms ease',
            '&:nth-of-type(even)': {
              backgroundColor: alpha(textPrimary, isDark ? 0.03 : 0.02),
            },
            '&:hover': {
              backgroundColor: rowHoverBackground,
            },
          },
          head: {
            backgroundColor: 'transparent',
            '&:nth-of-type(even)': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${alpha(divider, 0.25)}`,
            borderRight: 'none',
            paddingTop: 9,
            paddingBottom: 9,
            paddingLeft: 14,
            paddingRight: 14,
            fontSize: '0.875rem',
            lineHeight: 1.4,
            color: textPrimary,
            textAlign: 'left',
            '&:first-of-type': {
              paddingLeft: 18,
              borderLeft: 'none',
            },
            '&:last-child': {
              paddingRight: 18,
              borderRight: 'none',
            },
          },
          head: {
            fontWeight: 500,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: textSecondary,
            backgroundColor: 'transparent',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: { paddingTop: 7, paddingBottom: 7 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            minHeight: 36,
            paddingTop: 7,
            paddingBottom: 7,
            borderRadius: 0,
            transition: 'background-color 180ms ease, color 180ms ease',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36,
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: '0.875rem',
            fontWeight: 700,
          },
        },
      },
      MuiPaginationItem: {
        defaultProps: { size: 'medium' },
        styleOverrides: {
          root: { minWidth: 34, height: 34, borderRadius: 0 },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: { borderTop: `1px solid ${alpha(divider, 0.65)}` },
          toolbar: { minHeight: 44, paddingLeft: 8, paddingRight: 8 },
          selectLabel: { marginBottom: 0, fontSize: '0.875rem', color: textSecondary },
          displayedRows: { marginBottom: 0, fontSize: '0.875rem', color: textSecondary },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 0, fontWeight: 700, height: 28, fontSize: '0.75rem' },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            color: textPrimary,
            boxShadow: 'none',
            border: `1px solid ${surfaceBorder}`,
          },
          filled: { boxShadow: 'none' },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: { border: `1px solid ${surfaceBorder}`, borderRadius: 0, boxShadow: 'none' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 12,
            boxShadow: '0 18px 40px -24px rgba(15, 23, 42, 0.28)',
            background: menuBackground,
            backdropFilter: 'blur(10px)',
            marginTop: 6,
          },
          list: {
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginLeft: 8,
            marginRight: 8,
            marginTop: 3,
            marginBottom: 3,
            minHeight: 42,
            color: textPrimary,
            transition: 'background-color 180ms ease, transform 180ms ease',
            '&:hover': {
              backgroundColor: menuItemHoverBackground,
            },
            '&.Mui-selected': {
              backgroundColor: menuItemSelectedBackground,
            },
            '&.Mui-selected:hover': {
              backgroundColor: alpha(PRIMARY, isDark ? 0.3 : 0.18),
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { border: `1px solid ${surfaceBorder}`, borderRadius: 0, boxShadow: 'none' },
        },
      },
      MuiContainer: {
        defaultProps: { maxWidth: 'xl' },
        styleOverrides: {
          root: {
            paddingLeft: ADMIN_LAYOUT.containerPaddingPx.xs,
            paddingRight: ADMIN_LAYOUT.containerPaddingPx.xs,
            '@media (min-width:600px)': {
              paddingLeft: ADMIN_LAYOUT.containerPaddingPx.sm,
              paddingRight: ADMIN_LAYOUT.containerPaddingPx.sm,
            },
            '@media (min-width:900px)': {
              paddingLeft: ADMIN_LAYOUT.containerPaddingPx.md,
              paddingRight: ADMIN_LAYOUT.containerPaddingPx.md,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: { root: { display: 'none !important' } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { display: 'none !important' } },
      },
      MuiSkeleton: {
        styleOverrides: { root: { display: 'none !important' } },
      },
      MuiToolbar: {
        styleOverrides: { regular: { minHeight: 72 } },
      },
    },
  });
};

const theme = createAppTheme(
  typeof window === 'undefined'
    ? THEME_MODES.LIGHT
    : resolveThemeMode(window.localStorage.getItem(THEME_MODE_STORAGE_KEY))
);

export default theme;
